import {
  get,
  keyBy,
  chain,
  reduce,
  has,
  isEmpty,
  slice,
  reverse,
  last,
  map,
  isNumber
} from 'lodash';
import numeral from 'numeral';
import { formatValue } from '../../utils/report-builder';

const joinDimensionValues = (dimensions = {}, entry = {}) =>
  reduce(
    entry,
    (result, value, key) =>
      has(dimensions, key) ? [...result, isEmpty(value) ? '∅' : value] : result,
    []
  ).join(' - ');

// const truncateLabel = (label, length = 40) =>
//   label.length > length ? `${label.slice(0, length)  }...` : label;

const fieldsHelper = fields => {
  const fieldsByName = keyBy(fields, 'name');

  const { dimensions, metrics } = reduce(
    fieldsByName,
    (result, value, key) => {
      const name = last(key.split('.'));
      return get(value, 'fieldType') === 1
        ? {
            ...result,
            dimensions: { ...result.dimensions, [name]: { ...value, name } }
          }
        : {
            ...result,
            metrics: { ...result.metrics, [name]: { ...value, name } }
          };
    },
    { dimensions: {}, metrics: {} }
  );
  const axisLeftLegend = get(Object.values(metrics), [0, 'label']);
  const axisBottomLegend = chain(dimensions)
    .map(value => get(value, 'label'))
    .join(' - ')
    .value();

  const keys = [axisLeftLegend];
  const indexBy = 'label';

  return {
    dimensions,
    metrics,
    axisLeftLegend,
    axisBottomLegend,
    keys,
    indexBy
  };
};

export const parseColumnData = (fields = [], rawData = []) => {
  const {
    dimensions,
    metrics,
    axisLeftLegend,
    axisBottomLegend,
    keys,
    indexBy
  } = fieldsHelper(fields);

  const rawDataSubset = slice(rawData, 0, 49);

  const firstMetric = get(Object.values(metrics), [0], {});
  const firstMetricKey = get(Object.values(metrics), [0, 'name'], {});

  const format = value => formatValue(value, firstMetric);

  const data = reduce(
    rawDataSubset,
    (result, entry) => {
      const dimensionValues = joinDimensionValues(dimensions, entry);
      const metricValues = get(entry, firstMetricKey, 0);

      return [
        ...result,
        {
          label: dimensionValues,
          [axisLeftLegend]: metricValues
        }
      ];
    },
    []
  );

  return {
    axisLeftLegend,
    axisBottomLegend,
    format,
    tooltipFormat: format,
    keys,
    indexBy,
    data
  };
};

export const parseBarData = (fields = [], rawData = []) => {
  const {
    dimensions,
    metrics,
    axisLeftLegend,
    axisBottomLegend,
    keys,
    indexBy
  } = fieldsHelper(fields);

  const rawDataSubset = slice(rawData, 0, 49);

  const firstMetric = get(Object.values(metrics), [0], {});
  const firstMetricKey = get(Object.values(metrics), [0, 'name'], {});

  const format = value => formatValue(value, firstMetric);

  const data = reduce(
    rawDataSubset,
    (result, entry) => {
      const dimensionValues = joinDimensionValues(dimensions, entry);
      const metricValues = get(entry, firstMetricKey, 0);

      return [
        ...result,
        { label: dimensionValues, [axisLeftLegend]: metricValues }
      ];
    },
    []
  );

  return {
    axisLeftLegend,
    axisBottomLegend,
    format,
    tooltipFormat: format,
    keys,
    indexBy,
    data: reverse(data)
  };
};

export const parseLineData = (fields = [], rawData = []) => {
  const {
    dimensions,
    metrics,
    axisLeftLegend,
    axisBottomLegend,
    keys,
    indexBy
  } = fieldsHelper(fields);

  const rawDataSubset = slice(rawData, 0, 49);

  const firstMetric = get(Object.values(metrics), [0], {});
  const firstMetricKey = get(Object.values(metrics), [0, 'name'], {});

  const format = value => formatValue(value, firstMetric);

  const data = reduce(
    rawDataSubset,
    (result, entry) => {
      const dimensionValues = joinDimensionValues(dimensions, entry);
      const metricValues = get(entry, firstMetricKey, 0);

      return [...result, { x: dimensionValues, y: metricValues }];
    },
    []
  );

  return {
    axisLeftLegend,
    axisBottomLegend,
    format,
    keys,
    indexBy,
    data: [
      {
        id: 'id',
        data
      }
    ]
  };
};

export const parsePieData = (fields = [], rawData = []) => {
  const {
    dimensions,
    metrics,
    axisLeftLegend, // eslint-disable-line
    axisBottomLegend, // eslint-disable-line
    keys,
    indexBy
  } = fieldsHelper(fields);

  // nivo ResponsivePieCanvas doesn't support tooltipFormat so this a workaround to cut down visible decimals in
  // it's tooltip.
  rawData = map(rawData, entry =>
    reduce(
      entry,
      (memo, value, key) => {
        if (isNumber(value)) {
          const valueFormat = '0.00';
          value = numeral(value).format(valueFormat);
        }
        return { ...memo, ...{ [key]: value } };
      },
      {}
    )
  );

  const rawDataSubset = slice(rawData, 0, 24);
  const firstMetric = get(Object.values(metrics), [0], {});
  const firstMetricKey = get(Object.values(metrics), [0, 'name'], {});

  const sliceLabel = e => formatValue(e.value, firstMetric);

  const tooltipFormat = value => formatValue(value, firstMetric);

  const data = reduce(
    rawDataSubset,
    (result, entry) => {
      const dimensionValues = joinDimensionValues(dimensions, entry);
      const metricValues = get(entry, firstMetricKey, 0);

      return [
        ...result,
        { id: dimensionValues, label: dimensionValues, value: metricValues }
      ];
    },
    []
  );

  return {
    sliceLabel,
    tooltipFormat,
    keys,
    indexBy,
    data
  };
};
